import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  checkoutTransactionsSearch,
  checkoutTransactionsExcel,
} from '../../../redux/slices/checkout.slice'
import {allAccountLookup, basicLookup} from '../../../redux/slices/lookup.slice'

function List() {
  const intl = useIntl()
  const dispatch = useDispatch()

  const selectCheckoutData = (state) => ({
    transactions: state.checkout.transactions,
    loading: state.checkout.loading
  })

  const selectLookupData = (state) => ({
    allAccountLookupList: state.lookup.allAccountLookupList,
    CheckoutProcessType: state.lookup.CheckoutProcessType
  })

  const {transactions, loading} = useSelector(selectCheckoutData)
  const {allAccountLookupList, CheckoutProcessType} = useSelector(selectLookupData)

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) {
      dispatch(checkoutTransactionsSearch(params))
    }
  }, [dispatch, params])

  useEffect(() => {
    dispatch(allAccountLookup())
    dispatch(basicLookup('CheckoutProcessType'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const handleExcelExport = useCallback(() => {
    if (params) {
      dispatch(
        checkoutTransactionsExcel({
          params,
          fileName: intl.formatMessage({id: 'MENU.CASH.TRANSACTIONS'}),
        })
      )
    }
  }, [dispatch, params, intl])

  const Columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'TRANSACTIONS.GET.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCell item={props} onClick={(event) => event} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'TRANSACTIONS.GET.TOACCOUNTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'toAccountName',
      Cell: ({...props}) => <CustomCell item={props} onClick={(event) => event} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'TRANSACTIONS.GET.FROMACCOUNTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'fromAccountName',
      Cell: ({...props}) => <CustomCell item={props} onClick={(event) => event} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'TRANSACTIONS.GET.PAYMENTDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'paymentDate',
      Cell: ({...props}) => <CustomCell item={props} date='short' onClick={(event) => event} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'TRANSACTIONS.GET.CHECKOUTTYPENAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'checkoutProcessTypeName',
      Cell: ({...props}) => <CustomCell item={props} onClick={(event) => event} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'TRANSACTIONS.GET.ISOFFICIALNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'isOfficialName',
      Cell: ({...props}) => <CustomCell item={props} onClick={(event) => event} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'TRANSACTIONS.GET.AMOUNT'})}
          className='min-w-125px'
        />
      ),
      accessor: 'amount',
      Cell: ({...props}) => <CustomCell item={props} price onClick={(event) => event} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => (
        <CustomCellDouble item={props} double='lastModifiedDate' onClick={(event) => event} />
      ),
    },
  ], [intl, handleSorting])

  const buttons = useMemo(() => [], [])

  const actionButtons = useMemo(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'TRANSACTIONS.GET.TOACCOUNTNAME',
          data: allAccountLookupList,
          name: 'toAccountId',
        },
        {
          title: 'TRANSACTIONS.GET.FROMACCOUNTNAME',
          data: allAccountLookupList,
          name: 'fromAccountId',
        },
        {
          title: 'TRANSACTIONS.GET.CHECKOUTPROCESSTYPEID',
          data: CheckoutProcessType,
          name: 'checkoutProcessTypeId',
        },
        {
          title: 'TRANSACTIONS.GET.MINAMOUNT',
          type: 'number',
          name: 'minAmount',
        },
        {
          title: 'TRANSACTIONS.GET.MAXAMOUNT',
          type: 'number',
          name: 'maxAmount',
        },
        {
          title: 'TRANSACTIONS.GET.STARTDATE',
          type: 'date',
          name: 'beginDate',
        },
        {
          title: 'TRANSACTIONS.GET.ENDDATE',
          type: 'date',
          name: 'endDate',
        },
      ],
    },
  ], [allAccountLookupList, CheckoutProcessType, handleExcelExport])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'MENU.CASH.TRANSACTIONS'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages='TRANSACTIONS'
          buttons={buttons}
        />
        <KTCardBody>
          {!transactions && <ListLoading />}
          {transactions && (
            <Table
              items={transactions}
              cols={Columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
